/**
 * User Overrides Panel — flag-context driven.
 * Receives `flagKey` from the selected flag (no internal flag dropdown).
 * Uses UserSelector for multi-select user picking (no manual UUID input).
 */

import { useMemo, useState } from 'react';
import { useQueries } from '@tanstack/react-query';
import { Plus, Trash2, AlertCircle, User } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Switch } from '@/components/ui/Switch';
import { Textarea } from '@/components/ui/Textarea';
import { UserSelector } from '@/components/common/UserSelector';
import { UserContactInfo } from '@/components/common/UserContactInfo';
import { useI18n } from '@/i18n';
import { useFlagOverrides, useCreateOverride, useDeleteOverride } from '../hooks/useFeatureFlags';
import { getExpiryInfo } from '../utils/stale';
import { usersApi } from '@/lib/api/users';
import type { UserCompleteView } from '@/types/users';

// /users/complete caps page_size at 100, so resolve override users in <=100-id chunks.
const USER_LOOKUP_CHUNK = 100;

interface UserOverridesPanelProps {
  flagKey: string;
}

function OverrideExpiry({ expiresAt }: { expiresAt?: string }) {
  const { t } = useI18n();
  const { status, daysLeft } = getExpiryInfo(expiresAt);
  if (status === 'none') {
    return <span className="text-xs text-gray-400">{t('featureFlags.userOverrides.noExpiry')}</span>;
  }
  if (status === 'expired') {
    return (
      <span className="text-xs px-2 py-0.5 rounded bg-red-100 text-red-700">
        {t('featureFlags.userOverrides.expired')}
      </span>
    );
  }
  const label = t('featureFlags.userOverrides.daysLeft', { days: String(Math.max(0, daysLeft ?? 0)) });
  return (
    <span
      className={`text-xs px-2 py-0.5 rounded ${
        status === 'soon' ? 'bg-yellow-100 text-yellow-800' : 'bg-gray-100 text-gray-600'
      }`}
    >
      {label}
    </span>
  );
}

export function UserOverridesPanel({ flagKey }: UserOverridesPanelProps) {
  const { t } = useI18n();

  const { data: overrides, isLoading, error } = useFlagOverrides(flagKey);
  const createMutation = useCreateOverride();
  const deleteMutation = useDeleteOverride();

  // Resolve override users in batched chunks (avoids an N+1 per-row lookup AND the
  // 100-id page_size cap on /users/complete). One query per <=100-id chunk.
  const overrideIds = useMemo(() => (overrides ?? []).map((o) => o.user_id), [overrides]);
  const idChunks = useMemo(() => {
    const chunks: string[][] = [];
    for (let i = 0; i < overrideIds.length; i += USER_LOOKUP_CHUNK) {
      chunks.push(overrideIds.slice(i, i + USER_LOOKUP_CHUNK));
    }
    return chunks;
  }, [overrideIds]);
  const userQueries = useQueries({
    queries: idChunks.map((chunk) => ({
      queryKey: ['users', 'complete', { user_ids: chunk.join(','), page_size: chunk.length }],
      queryFn: () => usersApi.listUsersComplete({ user_ids: chunk.join(','), page_size: chunk.length }),
    })),
  });
  const userMap = new Map<string, UserCompleteView>();
  for (const q of userQueries) {
    for (const u of q.data?.users ?? []) userMap.set(u.user_id, u);
  }

  const [showAddDialog, setShowAddDialog] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [newEnabled, setNewEnabled] = useState(true);
  const [newReason, setNewReason] = useState('');
  const [newExpiresAt, setNewExpiresAt] = useState('');
  const [formError, setFormError] = useState('');

  const resetForm = () => {
    setSelectedIds([]);
    setNewReason('');
    setNewExpiresAt('');
    setNewEnabled(true);
    setFormError('');
  };

  const handleAddOverride = async () => {
    if (selectedIds.length === 0) {
      setFormError(t('featureFlags.userOverrides.selectUsersRequired'));
      return;
    }
    if (!newReason.trim()) {
      setFormError(t('featureFlags.userOverrides.reasonRequired'));
      return;
    }

    try {
      await createMutation.mutateAsync({
        flagKey,
        data: {
          user_ids: selectedIds,
          override_value: newEnabled,
          reason: newReason.trim(),
          expires_at: newExpiresAt ? new Date(newExpiresAt).toISOString() : undefined,
        },
      });
      setShowAddDialog(false);
      resetForm();
    } catch {
      setFormError(t('featureFlags.userOverrides.addFailed'));
    }
  };

  const handleDeleteOverride = async (userId: string) => {
    try {
      await deleteMutation.mutateAsync({ flagKey, userId });
    } catch (err) {
      toast.error(t('featureFlags.userOverrides.deleteFailed') + `: ${(err as Error).message}`);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-medium text-gray-900">{t('featureFlags.userOverrides.title')}</h3>
          <p className="text-sm text-gray-500">{t('featureFlags.userOverrides.description')}</p>
        </div>
        <Button variant="primary" onClick={() => setShowAddDialog(true)}>
          <Plus className="w-4 h-4 mr-2" />
          {t('featureFlags.userOverrides.addOverride')}
        </Button>
      </div>

      {/* Loading */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
            <div>
              <h3 className="text-red-800 font-semibold">{t('featureFlags.userOverrides.errorLoading')}</h3>
              <p className="text-red-600 text-sm">{(error as Error).message}</p>
            </div>
          </div>
        </div>
      )}

      {/* Overrides List */}
      {!isLoading && !error && overrides && (
        overrides.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <User className="w-12 h-12 mx-auto mb-2 opacity-50" />
            <p>{t('featureFlags.userOverrides.noOverrides')}</p>
            <p className="text-sm mt-1">{t('featureFlags.userOverrides.noOverridesHint')}</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100 border border-gray-200 rounded-lg overflow-hidden">
            {overrides.map((override) => (
              <OverrideRow
                key={override.user_id}
                userId={override.user_id}
                user={userMap.get(override.user_id)}
                value={!!override.override_value}
                reason={override.reason}
                expiresAt={override.expires_at}
                onDelete={() => handleDeleteOverride(override.user_id)}
                deleting={deleteMutation.isPending}
              />
            ))}
          </div>
        )
      )}

      {/* Add Override Dialog */}
      {showAddDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowAddDialog(false)} />
          <div className="relative bg-white rounded-lg shadow-xl w-full max-w-lg mx-4 p-6 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {t('featureFlags.userOverrides.addTitle')}
            </h3>

            <div className="space-y-4">
              {/* User multi-select */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('featureFlags.userOverrides.selectUsers')}
                  <span className="text-red-500 ml-1">*</span>
                </label>
                <UserSelector selectedUserIds={selectedIds} onChange={setSelectedIds} />
              </div>

              {/* Override Value */}
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-gray-700">
                  {t('featureFlags.userOverrides.overrideValue')}
                </label>
                <Switch
                  checked={newEnabled}
                  onChange={setNewEnabled}
                  ariaLabel={t('featureFlags.userOverrides.overrideValue')}
                  offLabel={t('featureFlags.userOverrides.disabled')}
                  onLabel={t('featureFlags.userOverrides.enabled')}
                />
              </div>

              {/* Reason (required) */}
              <Textarea
                label={t('featureFlags.userOverrides.reason')}
                required
                rows={2}
                value={newReason}
                onChange={(e) => setNewReason(e.target.value)}
                placeholder={t('featureFlags.userOverrides.reasonPlaceholder')}
              />

              {/* Expires At (optional) */}
              <Input
                type="date"
                label={t('featureFlags.userOverrides.expiresAt')}
                helperText={t('featureFlags.userOverrides.expiresAtHint')}
                value={newExpiresAt}
                onChange={(e) => setNewExpiresAt(e.target.value)}
              />

              {formError && <p className="text-sm text-red-600">{formError}</p>}
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <Button
                variant="secondary"
                onClick={() => {
                  setShowAddDialog(false);
                  setFormError('');
                }}
              >
                {t('common.cancel')}
              </Button>
              <Button variant="primary" onClick={handleAddOverride} loading={createMutation.isPending}>
                {t('featureFlags.userOverrides.addOverride')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function OverrideRow({
  userId,
  user,
  value,
  reason,
  expiresAt,
  onDelete,
  deleting,
}: {
  userId: string;
  user?: UserCompleteView;
  value: boolean;
  reason: string;
  expiresAt?: string;
  onDelete: () => void;
  deleting: boolean;
}) {
  const { t } = useI18n();

  return (
    <div className="flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors">
      <div className="flex items-center gap-3 min-w-0">
        <span className={`w-2 h-2 rounded-full flex-shrink-0 ${value ? 'bg-green-500' : 'bg-red-500'}`} />
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-sm text-gray-900">{user?.display_name ?? userId}</span>
            <span
              className={`text-xs px-2 py-0.5 rounded ${
                value ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
              }`}
            >
              {value ? t('featureFlags.userOverrides.enabled') : t('featureFlags.userOverrides.disabled')}
            </span>
            <OverrideExpiry expiresAt={expiresAt} />
          </div>
          {user && <UserContactInfo phone={user.phone_number} email={user.email} />}
          {!user && <p className="text-xs text-gray-400 font-mono mt-0.5 truncate">{userId}</p>}
          {reason && <p className="text-xs text-gray-500 mt-0.5">{reason}</p>}
        </div>
      </div>
      <button
        onClick={onDelete}
        className="p-1 text-gray-400 hover:text-red-600 rounded flex-shrink-0"
        disabled={deleting}
      >
        <Trash2 className="w-4 h-4" />
      </button>
    </div>
  );
}
